import { fetchPriceBookPrice, fetchPriceBookPrices } from './priceBookApi';
import {
  applyPriceBook,
  normalizeMerchantId,
  normalizeUpc,
  priceBookKey,
} from './priceBookCore';

const RESOLVED_TTL_MS = 5 * 60 * 1000;
const FAILED_TTL_MS = 60 * 1000;
// Cart and checkout reuse a price only this long: fresh enough to charge,
// without re-resolving on every one of the page's repeated cart fetches.
export const CHECKOUT_PRICE_MAX_AGE_MS = 30 * 1000;
const BULK_CHUNK_SIZE = 50;
const BATCH_WINDOW_MS = 50;
const DEFAULT_DEADLINE_MS = 8000;

const states = new Map();
const inflight = new Map();
const queues = new Map();
const listeners = new Set();

export const subscribePriceBook = (listener) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

export const getPriceBookState = (merchantId, upc) =>
  states.get(priceBookKey(merchantId, upc));

const setStates = (updates) => {
  if (updates.length === 0) return;
  updates.forEach(([key, state]) => states.set(key, state));
  listeners.forEach((listener) => listener());
};

const isFresh = (state, maxAgeMs) => {
  if (!state || state.status === 'pending') return false;
  const ttl =
    state.status === 'failed' ? Math.min(FAILED_TTL_MS, maxAgeMs) : maxAgeMs;
  return Date.now() - state.updatedAt < ttl;
};

const toUniqueUpcs = (upcs) => [
  ...new Set((Array.isArray(upcs) ? upcs : []).map(normalizeUpc).filter(Boolean)),
];

const toChunks = (list, size) => {
  const chunks = [];
  for (let index = 0; index < list.length; index += size) {
    chunks.push(list.slice(index, index + size));
  }
  return chunks;
};

const fetchChunk = (merchantId, upcs, mode) => {
  const request =
    mode === 'single' && upcs.length === 1
      ? fetchPriceBookPrice(merchantId, upcs[0])
      : fetchPriceBookPrices(merchantId, upcs);

  return Promise.resolve(request)
    .then((result) => {
      const updatedAt = Date.now();
      const entriesByUpc = new Map(
        (result?.entries || []).map((entry) => [entry.upc, entry]),
      );
      setStates(
        upcs.map((upc) => [
          priceBookKey(merchantId, upc),
          entriesByUpc.has(upc)
            ? { status: 'resolved', entry: entriesByUpc.get(upc), updatedAt }
            : { status: 'none', updatedAt },
        ]),
      );
    })
    .catch(() => {
      const updatedAt = Date.now();
      setStates(
        upcs.map((upc) => [
          priceBookKey(merchantId, upc),
          { status: 'failed', updatedAt },
        ]),
      );
    })
    .finally(() => {
      upcs.forEach((upc) => inflight.delete(priceBookKey(merchantId, upc)));
    });
};

export const ensurePriceBookPrices = async (
  merchantId,
  upcs,
  { maxAgeMs = RESOLVED_TTL_MS, mode = 'bulk' } = {},
) => {
  const merchant = String(merchantId ?? '').trim();
  const uniqueUpcs = toUniqueUpcs(upcs);
  if (!merchant || uniqueUpcs.length === 0) return;

  const waits = new Set();
  const upcsToFetch = [];
  uniqueUpcs.forEach((upc) => {
    const key = priceBookKey(merchant, upc);
    if (inflight.has(key)) {
      waits.add(inflight.get(key));
    } else if (!isFresh(states.get(key), maxAgeMs)) {
      upcsToFetch.push(upc);
    }
  });

  if (upcsToFetch.length > 0) {
    const updatedAt = Date.now();
    setStates(
      upcsToFetch.map((upc) => {
        const key = priceBookKey(merchant, upc);
        return [
          key,
          { status: 'pending', entry: states.get(key)?.entry, updatedAt },
        ];
      }),
    );

    toChunks(upcsToFetch, BULK_CHUNK_SIZE).forEach((chunk) => {
      const request = fetchChunk(merchant, chunk, mode);
      chunk.forEach((upc) => inflight.set(priceBookKey(merchant, upc), request));
      waits.add(request);
    });
  }

  await Promise.all(waits);
};

export const queuePriceBookPrices = (
  merchantId,
  upcs,
  { maxAgeMs = RESOLVED_TTL_MS } = {},
) => {
  const merchant = String(merchantId ?? '').trim();
  const uniqueUpcs = toUniqueUpcs(upcs);
  if (!merchant || uniqueUpcs.length === 0) return Promise.resolve();

  const queueKey = normalizeMerchantId(merchant);
  let queue = queues.get(queueKey);
  if (!queue) {
    queue = { merchant, upcs: new Set(), maxAgeMs };
    queue.done = new Promise((resolve) => {
      setTimeout(() => {
        queues.delete(queueKey);
        ensurePriceBookPrices(queue.merchant, [...queue.upcs], {
          maxAgeMs: queue.maxAgeMs,
        }).then(resolve);
      }, BATCH_WINDOW_MS);
    });
    queues.set(queueKey, queue);
  }

  uniqueUpcs.forEach((upc) => queue.upcs.add(upc));
  queue.maxAgeMs = Math.min(queue.maxAgeMs, maxAgeMs);
  return queue.done;
};

const readItemUpc = (item) => normalizeUpc(item?.upc ?? item?.upcCode);

export const applyPriceBookToItems = async (
  merchantId,
  items,
  {
    maxAgeMs = RESOLVED_TTL_MS,
    mode = 'bulk',
    batch = false,
    deadlineMs = DEFAULT_DEADLINE_MS,
  } = {},
) => {
  const list = Array.isArray(items) ? items : [];
  const upcs = list.map(readItemUpc).filter(Boolean);
  if (!merchantId || upcs.length === 0) return list;

  const request = batch
    ? queuePriceBookPrices(merchantId, upcs, { maxAgeMs })
    : ensurePriceBookPrices(merchantId, upcs, { maxAgeMs, mode });
  let deadlineTimer;
  await Promise.race([
    request,
    new Promise((resolve) => {
      deadlineTimer = setTimeout(resolve, deadlineMs);
    }),
  ]);
  clearTimeout(deadlineTimer);

  return list.map((item) => {
    const upc = readItemUpc(item);
    if (!upc) return item;
    return applyPriceBook(item, getPriceBookState(merchantId, upc)?.entry);
  });
};
